/**
 * TopRisksPage — focused list of the highest-exposure risks across the org.
 *
 * Hits `orgRiskDashboardUrl?ordering=-exposure&limit=15` (defaults; the user
 * can change the limit and level filter). Reuses the same `top_risks` shape
 * as the OrgRiskDashboard.
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
  Button,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import RefreshIcon from "@mui/icons-material/Refresh";
import TimelineIcon from "@mui/icons-material/Timeline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import PageTitle from "../../../components/Page Component/PageTitle";
import { fetchData } from "../../../store/actions";
import { RootState } from "../../../store/Store";
import { useCustomToast } from "../../../components/Methods/SnackBar";
import CustomNoRowsOverlay from "../../../components/Data/NoRowsOverlayComponent";
import { orgRiskDashboardUrl } from "../../../Service/Apis";
import { OrgRiskAggregate, RiskLevel } from "./Types/RiskManagementTypes";
import RiskScoreBadge from "./components/RiskScoreBadge";
import RiskStatusChip from "./components/RiskStatusChip";
import { isRmwOrgViewer } from "./components/rmwRoles";

const LIMIT_OPTIONS = [10, 15, 25, 50];

const TopRisksPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const selectedRole = useSelector(
    (state: RootState) => state.auth.selectedRole
  );

  useEffect(() => {
    if (!isRmwOrgViewer(selectedRole)) {
      navigate("/landing", { replace: true });
    }
  }, [selectedRole, navigate]);

  const [data, setData] = useState<OrgRiskAggregate | null>(null);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(15);
  const [level, setLevel] = useState<RiskLevel | "ALL">("ALL");

  const fetchData$ = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("ordering", "-exposure");
    params.set("limit", String(limit));
    if (level !== "ALL") params.set("level", level);
    if (selectedRole) params.set("role", selectedRole);
    dispatch(fetchData(`${orgRiskDashboardUrl}?${params.toString()}`))
      .then((res: any) => setData(res as OrgRiskAggregate))
      .catch(() => showToast("Failed to load top risks.", "error", false))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, limit, level, selectedRole]);

  useEffect(() => {
    fetchData$();
  }, [fetchData$]);

  const rows = (data?.top_risks ?? []).map((r) => ({ id: r.risk_id, ...r }));

  const columns: GridColDef[] = [
    {
      field: "rank",
      headerName: "#",
      width: 60,
      valueGetter: (p) => rows.findIndex((r) => r.id === p.row.id) + 1,
    },
    { field: "project_name", headerName: "Project", flex: 1.2, minWidth: 180 },
    { field: "area_name", headerName: "Area", width: 130 },
    { field: "contributor_name", headerName: "Contributor", width: 160 },
    { field: "pm_name", headerName: "PM", width: 160 },
    {
      field: "level",
      headerName: "Level / Exposure",
      width: 160,
      renderCell: (p: GridRenderCellParams) => (
        <RiskScoreBadge level={p.value as any} exposure={p.row.exposure} />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: (p: GridRenderCellParams) => (
        <RiskStatusChip status={p.value as string} />
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 96,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      // Icon-only buttons (with tooltips) so both actions fit comfortably
      // inside a narrow column without overlapping.
      renderCell: (p) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="View risk trend">
            <IconButton
              size="small"
              onClick={() =>
                navigate(`/pms/risk-management/risk/${p.row.risk_id}/history`)
              }
            >
              <TimelineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Open project summary">
            <IconButton
              size="small"
              onClick={() =>
                navigate(`/pms/risk-management/summary/${p.row.project_id}`)
              }
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 10, mb: 6 }}>
      <PageTitle>Top Risks</PageTitle>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Show top</InputLabel>
              <Select
                label="Show top"
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
              >
                {LIMIT_OPTIONS.map((o) => (
                  <MenuItem key={o} value={o}>
                    Top {o}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Level</InputLabel>
              <Select
                label="Level"
                value={level}
                onChange={(e) =>
                  setLevel(e.target.value as RiskLevel | "ALL")
                }
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="HIGH">High only</MenuItem>
                <MenuItem value="MEDIUM">Medium only</MenuItem>
                <MenuItem value="LOW">Low only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md sx={{ textAlign: "right" }}>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchData$}
              disabled={loading}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Highest-exposure risks across the organization
        </Typography>
        <Box sx={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            slots={{ noRowsOverlay: CustomNoRowsOverlay }}
            initialState={{
              pagination: { paginationModel: { pageSize: 25, page: 0 } },
            }}
            pageSizeOptions={[25, 50, 100]}
          />
        </Box>
      </Paper>
    </Container>
  );
};

export default TopRisksPage;
